import { SectionHeader } from "./SectionHeader";
import { ConfigParamRow } from "./ConfigParamRow";
import {
  getParameterDisplayName,
  getParameterDescription,
} from "@/utils/parameterNames";

interface FiltersSettingsSectionProps {
  parameters: Record<string, number | [number, number] | boolean>;
  pinnedParameters: Record<string, boolean>;
}

// Filters parameters in display order (matches LC3 indicator structure).
//
// The numeric filter parameters (emaperiod, smaperiod, regimethreshold,
// adxthreshold) are optimization RANGES stored as [min, max] tuples, not single
// values. They must use type "range" so ConfigParamRow renders "[min, max]";
// rendering them as "number" caused React to concatenate the tuple into
// unreadable digits (e.g. [10, 600] -> "10600", [-2.5, 2.5] -> "-2.52.5").
const FILTERS_PARAMETERS = [
  { key: "useemafilter", type: "boolean" as const },
  { key: "emaperiod", type: "range" as const },
  { key: "usesmafilter", type: "boolean" as const },
  { key: "smaperiod", type: "range" as const },
  { key: "usevolatilityfilter", type: "boolean" as const },
  { key: "useregimefilter", type: "boolean" as const },
  { key: "regimethreshold", type: "range" as const },
  { key: "useadxfilter", type: "boolean" as const },
  { key: "adxthreshold", type: "range" as const },
] as const;

/**
 * Get the ternary state for a boolean parameter:
 * - "optimize": Not pinned, will be tested in both on/off states
 * - "on": Pinned and set to true
 * - "off": Pinned and set to false
 */
const getBooleanState = (
  key: string,
  parameters: Record<string, number | [number, number] | boolean>,
  pinnedParameters: Record<string, boolean>
): "off" | "optimize" | "on" => {
  const isPinned = pinnedParameters[key] ?? false;
  if (!isPinned) return "optimize";
  return parameters[key] ? "on" : "off";
};

export function FiltersSettingsSection({
  parameters,
  pinnedParameters,
}: FiltersSettingsSectionProps) {
  return (
    <div>
      <SectionHeader title="Filters Settings" gradient="emerald-teal" />
      <div className="space-y-2">
        {FILTERS_PARAMETERS.map(({ key, type }) => {
          const value = parameters[key];
          if (value === undefined) return null;

          const displayName = getParameterDisplayName(key);
          const description = getParameterDescription(key);
          const isPinned = pinnedParameters[key] === true;

          if (type === "boolean") {
            const booleanState = getBooleanState(key, parameters, pinnedParameters);
            return (
              <ConfigParamRow
                key={key}
                label={displayName}
                description={description}
                value={Boolean(value)}
                isPinned={isPinned}
                isOptimizing={booleanState === "optimize"}
                type="boolean"
              />
            );
          }

          // Range parameters (emaperiod, smaperiod, regimethreshold,
          // adxthreshold): values are [min, max] tuples, or a single number
          // when pinned. ConfigParamRow renders these as "[min, max]".
          const isRange = Array.isArray(value) && value[0] !== value[1];
          return (
            <ConfigParamRow
              key={key}
              label={displayName}
              description={description}
              value={value as number | [number, number]}
              isPinned={isPinned}
              isOptimizing={!isPinned && isRange}
              type="range"
            />
          );
        })}
      </div>
    </div>
  );
}
